import { ButtonProtected } from '@components'

import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { UiSource } from '@features/reconciliation/enums'
import { useRejectSuggestion } from '@features/reconciliation/hooks/useRejectSuggestion'
import { ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../../../contexts'
import { useSandboxAwareTrack } from '../../../../hooks/useSandboxAwareTrack'

interface SuggestionIgnoreButtonProps {
  matchSuggestion?: ReconciliationSuggestion
}

export const SuggestionIgnoreButton = ({ matchSuggestion }: SuggestionIgnoreButtonProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSandboxAwareTrack()
  const sandbox = useSandboxModeContext()
  const { hasBankReconciliationWriteScope, setMatchError } = useBankTransactionDrawer()
  const { rejectSuggestion, isRejecting } = useRejectSuggestion({
    onError: (errorMessage) => {
      setMatchError(errorMessage || t('something_went_wrong'))
    },
    uiSource: UiSource.AccountingTransactionView,
  })

  const handleIgnoreClick = useCallback(() => {
    if (matchSuggestion) {
      const bankLineId = matchSuggestion.transactions[0]?.id
      if (sandbox && bankLineId) {
        sandbox.dismissSuggestionInSandbox(bankLineId)
        return
      }

      rejectSuggestion({ matchSuggestion })
      track('XXX - Reconciliation - Reconciliation Suggestion Rejected (Client)')
    }
  }, [matchSuggestion, sandbox, rejectSuggestion, track])

  return (
    <ButtonProtected
      variant="outlined"
      size="sm"
      fullWidth
      onClick={handleIgnoreClick}
      disabled={hasBankReconciliationWriteScope && isRejecting}
      scopes={Scope.BankReconciliationWrite}
      trackingContext={TrackingContext.BankTransactions}
    >
      {t('bank_transactions.drawer.suggestion_ignore')}
    </ButtonProtected>
  )
}
